import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getProfile,
  updateProfile,
  uploadProfilePic,
} from "../../../services/panel/panelServices";
import KycStatusPill from "../../component/KycStatusPill";
import { ROLES } from "../../../context/AuthContext";
import toast from "../../../utils/toast";

// key = field in Profile/Get `profile`, payload = key sent to Profile/Update
const SECTIONS = [
  {
    title: "Business",
    fields: [
      { key: "name", payload: "Name", label: "Name" },
      { key: "outletName", payload: "OutletName", label: "Business name" },
      { key: "emailID", payload: "EmailID", label: "Email", type: "email" },
      { key: "dob", payload: "DOB", label: "Date of birth", type: "date" },
      {
        key: "companyTypeID",
        payload: "CompanyTypeID",
        label: "Company type",
        type: "companyType",
      },
    ],
  },
  {
    title: "Contact and address",
    fields: [
      {
        key: "alternateMobile",
        payload: "AlternateMobile",
        label: "Alternate mobile",
        maxLength: 10,
      },
      {
        key: "whatsAppNumber",
        payload: "WhatsAppNumber",
        label: "WhatsApp",
        maxLength: 10,
      },
      { key: "pincode", payload: "Pincode", label: "Pincode", maxLength: 6 },
      // Not in the guide's editable list — server may still set these from the pincode
      { key: "city", payload: "City", label: "City" },
      { key: "stateName", payload: "StateName", label: "State" },
      {
        key: "latlong",
        payload: "Latlong",
        label: "Latlong",
        placeholder: "19.0760,72.8777",
      },
      {
        key: "address",
        payload: "Address",
        label: "Address",
        maxLength: 300,
        wide: true,
      },
    ],
  },
  {
    title: "Identity",
    fields: [
     
      {
        key: "pan",
        payload: "PAN",
        label: "PAN",
        maxLength: 10,
        upper: true,
      },
      {
        key: "aadhar",
        payload: "AADHAR",
        label: "Aadhaar",
        maxLength: 12,
      },
      {
        key: "gstin",
        payload: "GSTIN",
        label: "GSTIN",
        maxLength: 15,
        upper: true,
      },
    ],
  },
  {
    title: "Bank account",
    fields: [
      { key: "bankName", payload: "BankName", label: "Bank", type: "bank" },
      { key: "branchName", payload: "BranchName", label: "Branch" },
      {
        key: "ifsc",
        payload: "IFSC",
        label: "IFSC",
        maxLength: 11,
        upper: true,
      },
      {
        key: "accountNumber",
        payload: "AccountNumber",
        label: "Account number",
      },
      {
        key: "accountName",
        payload: "AccountName",
        label: "Account holder name",
      },
    ],
  },
];

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

const ROLE_NAMES = {
  [ROLES.FLEET_OWNER]: "Fleet Owner",
  [ROLES.CHECKER]: "Checker",
  [ROLES.MAKER]: "Maker",
};

const roleName = (roleID) =>
  ROLE_NAMES[roleID] || (roleID ? `Role ${roleID}` : "—");

// "/Image/Profile/2.png" is served from the API host root (not /WebApp)
const toImageUrl = (path) =>
  !path || path.startsWith("http")
    ? path
    : `https://${process.env.REACT_APP_API_DOMAIN}${path}`;

const initials = (name = "") =>
  name
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

// Profile pic, or the name initials when there is none / it fails to load
const Avatar = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const box = {
    width: 56,
    height: 56,
    borderRadius: "50%",
    flexShrink: 0,
    border: "1px solid var(--hair, rgba(14,38,72,.1))",
  };
  return src && !failed ? (
    <img
      src={src}
      alt={name || "Profile"}
      onError={() => setFailed(true)}
      style={{ ...box, objectFit: "cover" }}
    />
  ) : (
    <div
      style={{
        ...box,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 18,
        color: "#fff",
        background:
          "linear-gradient(150deg, var(--violet-light), var(--violet-dark))",
      }}
    >
      {initials(name)}
    </div>
  );
};

// Server-only values from Profile/Get — not sent in Update
const InfoField = ({ label, hint, children }) => (
  <div className="field">
    <label>
      {label}
      {hint && (
        <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>
          {" "}
          · {hint}
        </span>
      )}
    </label>
    {children}
  </div>
);

// DOB may come as "1988-04-25" or "04/25/1988 12:00:00 AM" — date input needs yyyy-MM-dd
const toDateInput = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toForm = (profile = {}) => {
  const form = { isRegisteredWithGST: !!profile.isRegisteredWithGST };
  ALL_FIELDS.forEach((f) => {
    const value = profile[f.key] ?? "";
    if (f.type === "date") form[f.key] = toDateInput(value);
    // companyTypeID 0 = not chosen yet
    else if (f.type === "companyType") form[f.key] = value ? String(value) : "";
    else form[f.key] = String(value).trim();
  });
  return form;
};

const ageOn = (dateStr) => {
  const dob = new Date(dateStr);
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
};

const validate = (form) => {
  const e = {};
  const v = (k) => form[k].trim();
  const nameRule = (k, label) => {
    if (!v(k)) e[k] = `${label} is required.`;
    else if (!isNaN(v(k))) e[k] = `${label} can't be a number.`;
    else if (v(k).length > 100) e[k] = "At most 100 characters.";
  };
  nameRule("name", "Name");
  nameRule("outletName", "Business name");
  if (!/^\S+@\S+\.\S+$/.test(v("emailID"))) e.emailID = "Enter a valid email.";
  if (!v("dob")) e.dob = "Date of birth is required.";
  else if (ageOn(v("dob")) < 18 || ageOn(v("dob")) > 100)
    e.dob = "Age must be between 18 and 100.";
  if (v("alternateMobile") && !/^\d{10}$/.test(v("alternateMobile")))
    e.alternateMobile = "Enter a 10-digit number.";
  if (v("whatsAppNumber") && !/^\d{10}$/.test(v("whatsAppNumber")))
    e.whatsAppNumber = "Enter a 10-digit number.";
  if (!/^\d{6}$/.test(v("pincode"))) e.pincode = "Enter a 6-digit pincode.";
  if (v("latlong") && !/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(v("latlong")))
    e.latlong = "Use latitude,longitude e.g. 19.0760,72.8777";
  if (!v("address")) e.address = "Address is required.";
  if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(v("pan").toUpperCase()))
    e.pan = "Enter a valid PAN (AAAAA9999A).";
  if (!/^\d{12}$/.test(v("aadhar"))) e.aadhar = "Enter a 12-digit Aadhaar.";
  if (form.isRegisteredWithGST && v("gstin").length !== 15)
    e.gstin = "GSTIN must be 15 characters.";
  if (!/^[A-Za-z ]+$/.test(v("accountName")))
    e.accountName = "Letters and spaces only.";
  return e;
};

export default function ProfileDetails() {
  const navigate = useNavigate();
  const [isProfileData, setProfileData] = useState(null);
  const [isBankData, setBankData] = useState([]);
  const [isCompanyTypeData, setCompanyTypeData] = useState([]);
  const [lockedFields, setLockedFields] = useState([]);
  const [form, setForm] = useState(toForm());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picPreview, setPicPreview] = useState("");
  const [picUploading, setPicUploading] = useState(false);
  // Re-upload can keep the same path ("/Image/Profile/2.png") — bust the cache
  const [picVersion, setPicVersion] = useState(0);
  const picInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await getProfile();

      if (res?.statuscode === 1) {
        setProfileData(res?.profile || {});
        // Some bank names come with trailing spaces / duplicates
        setBankData([...new Set((res?.banks || []).map((b) => b.trim()))]);
        setCompanyTypeData(res?.companyTypes || []);
        setLockedFields((res?.lockedFields || []).map((f) => f.toLowerCase()));
        setForm(toForm(res?.profile));
        setErrors({});
      } else {
        toast.error(res?.msg || "Failed to load profile.");
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch profile",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const isLocked = (key) => lockedFields.includes(key.toLowerCase());

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const handleSave = async () => {
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) return;

    // Only changed, unlocked fields — the API keeps whatever is left out
    const original = toForm(isProfileData);
    const payload = {};
    ALL_FIELDS.forEach((f) => {
      if (isLocked(f.key)) return;
      let value = form[f.key].trim();
      if (f.upper) value = value.toUpperCase();
      if (value === original[f.key].trim()) return;
      payload[f.payload] = f.type === "companyType" ? Number(value) : value;
    });
    if (form.isRegisteredWithGST !== original.isRegisteredWithGST) {
      payload.IsRegisteredWithGST = form.isRegisteredWithGST;
    }
    if (!Object.keys(payload).length) {
      toast.info("Nothing to update.");
      return;
    }

    setSaving(true);
    try {
      const res = await updateProfile(payload);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Profile updated.");
        fetchProfile(); // Update returns no profile data
      } else {
        toast.error(res?.msg || "Failed to update profile.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const locked = isLocked(f.key);
    const disabled = locked || loading || saving;
    const style = errors[f.key] ? { borderColor: "#dc2626" } : {};
    return (
      <div
        className="field"
        key={f.key}
        style={f.wide ? { gridColumn: "1 / -1" } : undefined}
      >
        <label>
          {f.label}
          {locked && (
            <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>
              {" "}
              · locked (verified)
            </span>
          )}
        </label>
        {f.type === "companyType" ? (
          <select
            name={f.key}
            value={form[f.key]}
            onChange={handleChange}
            disabled={disabled}
            style={style}
          >
            <option value="">Select company type</option>
            {isCompanyTypeData.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}
              </option>
            ))}
          </select>
        ) : f.type === "bank" ? (
          <select
            name={f.key}
            value={form[f.key]}
            onChange={handleChange}
            disabled={disabled}
            style={style}
          >
            <option value="">Select bank</option>
            {/* keep a saved bank visible even if it's not in the list */}
            {form[f.key] && !isBankData.includes(form[f.key]) && (
              <option value={form[f.key]}>{form[f.key]}</option>
            )}
            {isBankData.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={f.type === "date" ? "date" : "text"}
            name={f.key}
            value={form[f.key]}
            onChange={handleChange}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            readOnly={locked}
            disabled={disabled}
            style={{
              ...style,
              ...(f.upper ? { textTransform: "uppercase" } : {}),
            }}
          />
        )}
        {errors[f.key] && (
          <span style={{ color: "#dc2626", fontSize: 11 }}>
            {errors[f.key]}
          </span>
        )}
      </div>
    );
  };

  const handlePicChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only .jpg, .jpeg or .png images are allowed.");
      return;
    }

    const preview = URL.createObjectURL(file);
    setPicPreview(preview);
    setPicUploading(true);
    try {
      const res = await uploadProfilePic(file);
      if (res?.statuscode === 1) {
        toast.success(res?.msg || "Profile photo updated.");
        setPicVersion(Date.now());
        fetchProfile();
      } else {
        toast.error(res?.msg || "Failed to update profile photo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to update profile photo.");
    } finally {
      URL.revokeObjectURL(preview);
      setPicPreview("");
      setPicUploading(false);
    }
  };

  const picUrl = isProfileData?.profilePic
    ? toImageUrl(isProfileData.profilePic) +
      (picVersion ? "?v=" + picVersion : "")
    : "";

  return (
    <div className="card">
      <div className="card-head-row">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            onClick={() => picInputRef.current?.click()}
            disabled={picUploading || loading}
            title="Change profile photo"
            style={{
              position: "relative",
              padding: 0,
              border: 0,
              background: "none",
              borderRadius: "50%",
              cursor: picUploading ? "wait" : "pointer",
              opacity: picUploading ? 0.6 : 1,
            }}
          >
            <Avatar src={picPreview || picUrl} name={isProfileData?.name} />
            <span
              style={{
                position: "absolute",
                right: -2,
                bottom: -2,
                width: 22,
                height: 22,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                color: "#fff",
                background: "var(--violet-dark)",
                border: "2px solid #fff",
              }}
            >
              {picUploading ? "…" : "✎"}
            </span>
          </button>
          <input
            ref={picInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={handlePicChange}
          />
          <div>
            <div className="card-title">
              {isProfileData?.outletName || "Company details"}
            </div>
            <div className="card-sub">
              {loading
                ? "Loading profile..."
                : `User ID ${isProfileData?.userID ?? "—"} · ${roleName(
                    isProfileData?.roleID,
                  )}`}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>KYC</span>
          <KycStatusPill status={isProfileData?.kycStatus} />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate("/kyc-documents")}
          >
            Complete KYC
          </button>
        </div>
      </div>

      {SECTIONS.map((s) => (
        <div key={s.title} style={{ marginTop: 10 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: ".04em",
              color: "var(--text-faint)",
              marginBottom: 6,
            }}
          >
            {s.title}
          </div>
          <div className="form-grid form-grid-4">
            {/* Login id — shown only, Profile/Update doesn't accept it */}
            {s.title === "Contact and address" && (
              <InfoField label="Mobile" hint="login id">
                <input
                  value={isProfileData?.mobileNo || ""}
                  readOnly
                  disabled
                />
              </InfoField>
            )}
            {s.fields.map(renderField)}
            {s.title === "Identity" && (
              <div className="field">
                <label>Registered with GST</label>
                <label
                  className="checkbox-row"
                  style={{ paddingTop: 8, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    name="isRegisteredWithGST"
                    checked={form.isRegisteredWithGST}
                    onChange={handleChange}
                    disabled={loading || saving}
                  />{" "}
                  Yes
                </label>
              </div>
            )}
          </div>
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          className="btn btn-ghost"
          onClick={fetchProfile}
          disabled={loading || saving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? "Updating..." : "Update profile"}
        </button>
      </div>
    </div>
  );
}
